import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import isEqual from 'lodash/isEqual';
import { Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ConfirmDialog, DialogService } from '../../dialogs';
import { coerceConfig, getConfigArrayValidators, getConfigValidators, isConfigArray } from '../gear.utils';
import { GearConfig, GearConfigInvocation } from '../../shared/models/gear.model';
import { replayValue } from '../../shared/observable.utils';
import { TranslationBundle, TranslationService } from '../../translation.service';

@Component({
  host: {
    'test-id': 'gear-config',
  },
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    useExisting: forwardRef(() => GearConfigComponent),
    multi: true,
  }],
  selector: 'gear-config',
  styleUrls: ['./gear-config.component.scss'],
  templateUrl: './gear-config.component.html',
})
export class GearConfigComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() config: GearConfig = {}

  form = new FormGroup({})

  private messages = {
    config: TranslationBundle.EMPTY,
  }
  private subscriptions = {
    messages: Subscription.EMPTY,
  }

  /* eslint-disable @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars */
  private onChange = (value: GearConfigInvocation): void => {}
  private onTouched = (): void => {}
  /* eslint-enable @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars */

  constructor(
    private dialog: DialogService,
    private translate: TranslationService,
  ) {
    this.subscriptions.messages = this.translate.getBundle('gearConfig').subscribe(configMessages => {
      this.messages.config = configMessages;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config) {
      const prevConfig: GearConfig = changes.config.previousValue;
      const prevValues: GearConfigInvocation = this.form.value;
      const wasDisabled = this.form.disabled;
      this.form = new FormGroup(Object.assign({},
        ...Object.entries(this.config ?? {})
          .map(([key, config]) => {
            let _default = config.default;
            if (config.type === 'boolean' && typeof _default !== 'boolean') {
              _default = false;
            }
            // restore values that were not backed by a config, or backed by the same config
            if (key in prevValues && (!(key in prevConfig) || isEqual(config, prevConfig[key]))) {
              _default = prevValues[key];
            }
            if (isConfigArray(config)) {
              return { [key]: new FormControl(_default || [], getConfigArrayValidators(config)) };
            }
            return { [key]: new FormControl(_default, getConfigValidators(config)) };
          })
      ));
      this.form.markAllAsTouched(); // light up required
      if (wasDisabled) {
        this.form.disable();
      }
      replayValue(this.form).pipe(
        delay(0), // allow emit during writeValue
      ).subscribe(values => {
        this.onChange(coerceConfig(values, this.config));
      });
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get hasErrors(): boolean {
    return this.form.invalid;
  }

  getPlaceholder(key: string): string {
    const hasDefault = 'default' in this.config[key];
    if (hasDefault && this.config[key].default !== '') {
      return `Default: ${this.config[key].default}`;
    }
    return '';
  }

  getError(key: string): string {
    const { errors, value } = this.form.get(key);
    if (!errors) {
      return '';
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-const
    let [validatorKey, validatorValue]: [string, any] = Object.entries(errors.arrayItems ?? errors)[0];
    if (Array.isArray(value) && !errors.arrayItems) {
      validatorKey = {
        'minlength': 'minItems',
        'maxlength': 'maxItems',
      }[validatorKey] ?? validatorKey;
    }
    let message = this.messages.config.get(['validation', validatorKey]);
    if (['minlength', 'maxlength', 'minItems', 'maxItems'].includes(validatorKey)) {
      message = this.translate.interpolate(message, validatorValue.requiredLength);
    } else if (validatorKey === 'pattern') {
      message = this.translate.interpolate(message, validatorValue.requiredPattern);
    } else if (validatorKey === 'min') {
      message = this.translate.interpolate(message, validatorValue.min);
    } else if (validatorKey === 'max') {
      message = this.translate.interpolate(message, validatorValue.max);
    }
    if (errors.arrayItems) {
      const [position, value] = validatorValue;
      return `${value || position}: ${message || validatorKey}`;
    }
    return message;
  }

  getDefaults(config: GearConfig = this.config): GearConfigInvocation {
    return Object.assign({},
      ...Object.entries(config ?? {})
        .map(([key, config]) => {
          let _default = config.default;
          if (config.type === 'boolean' && typeof _default !== 'boolean') {
            _default = false;
          }
          if (config.type === 'array' && !Array.isArray(_default)) {
            _default = [];
          }
          return { [key]: _default };
        })
    );
  }

  resetToDefaults(): void {
    const values = this.getDefaults();
    for (const key of Object.keys(values)) {
      if (typeof values[key] === 'undefined') {
        values[key] = null; // setValue / reset does not accept undefined
      }
    }
    this.form.setValue(values);
  }

  confirmReset(): void {
    this.dialog.open(ConfirmDialog, {
      data: { content: this.messages.config.get('reset.confirm') },
    }).afterClosed().subscribe(proceed => {
      if (proceed) {
        this.resetToDefaults();
      }
    });
  }

  /** ControlValueAccessor **/
  writeValue(value: GearConfigInvocation): void {
    for (const key of Object.keys(value ?? {})) {
      // if we receive values before config, store them in a new control
      if (!this.form.contains(key)) {
        this.form.addControl(key, new FormControl());
      }
    }
    this.form.patchValue(value);
  }

  registerOnChange(fn: (value: GearConfigInvocation) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(toDisabled: boolean): void {
    this.form[toDisabled ? 'disable' : 'enable']();
  }
}
